import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

import { getStoredToken } from "../utils/getStoredToken";
import { PartnerSafeApi } from './EnvFiles';

// Função de comparação para ordenação
const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const UserList = ({ changeSelectedUser }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [roles, setRoles] = useState(null);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('nome');
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchMyData = async () => {
    const token = getStoredToken();

    try {
      const response = await axios.get(
        `${PartnerSafeApi}/api/v1/Contas/MeusDados`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = response.data;

      const currentIsAdmin = data.roles.some(
        (role) => ['Admin', 'BackOffice'].includes(role.nome),
      );

      if (currentIsAdmin) {
        setIsAdmin(true)
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Erro ao buscar dados do usuário:', err);
    }
  };

  useEffect(() => {
    fetchMyData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);

        const token = getStoredToken();
        const response = await axios.get(`${PartnerSafeApi}/api/v1/Contas/ListarUsuarios`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setUsers(response.data);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);

        setError('Não foi possível carregar os dados. Verifique o servidor.');
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = getStoredToken();
        const response = await axios.get(`${PartnerSafeApi}/api/v1/Contas/ListarRoles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Roles fetched:', response.data);
        setRoles(response.data);
      } catch (error) {
        console.error('Erro ao buscar roles:', error);
      }
    };
    fetchRoles();
  }, []);

  const isLoading = useMemo(() => (
    !isAdmin || roles === null || users === null
  ), [isAdmin, roles, users]);

  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setUserToEdit(null);
  };

  const handleCentrosServicoChange = (event) => {
    const {
      target: { value },
    } = event;
    setUserToEdit((prev) => ({
      ...prev,
      // Ensure it's an array
      des_centro_servico: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleChipDelete = (chipToDelete) => {
    setUserToEdit((prev) => ({
      ...prev,
      des_centro_servico: prev.des_centro_servico.filter((id) => id !== chipToDelete),
    }));
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${PartnerSafeApi}/usuarios/${userToDelete.email}`);

      setUsers(users.filter((user) => user.email !== userToDelete.email));
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      alert('Erro ao excluir usuário. Tente novamente.');
    }
  };

  const handleSaveEdit = async () => {
    if (!userToEdit) return;

    try {
      const token = getStoredToken();
      const payload = {
        UsuarioIdentificador: userToEdit.identificador,
        RolesIdentificadores: userToEdit.des_centro_servico,
      };

      console.log('Payload being sent to /api/v1/Contas/AssociarRole:', payload);

      await axios.post(`${PartnerSafeApi}/api/v1/Contas/AssociarRole`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSnackbarMessage('Perfil de acesso atualizado com sucesso!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseEditDialog();
      setTimeout(() => {
        // Reload the page to simulate F5
        window.location.reload(); 
      // Delay to allow the snackbar to be visible
      }, 1000);
    } catch (error) {
      console.error('Erro ao associar role:', error);
      setSnackbarMessage('Erro ao atualizar perfil de acesso. Tente novamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectUser = (user) => {
    changeSelectedUser(user);
    navigate('/users-panel/edit')
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress
          size={60}
          sx={{
            color: 'primary.main',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Fade in timeout={500}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Card>
          <CardHeader
            title={
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: '#40685E' }}
                >
                  Gestão de Usuários
                </Typography>
                {/* <Button
                  variant="contained"
                  onClick={() => navigate('/users-panel/create')}
                >
                  Criar Usuário
                </Button> */}
              </Box>
            }
          />
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'nome'}
                      direction={orderBy === 'nome' ? order : 'asc'}
                      onClick={() => handleRequestSort('nome')}
                    >
                      Nome
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'perfil'}
                      direction={orderBy === 'perfil' ? order : 'asc'}
                      onClick={() => handleRequestSort('perfil')}
                    >
                      Perfil
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleRequestSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {error ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {error}
                    </TableCell>
                  </TableRow>
                ) : users && users?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhum usuário encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  stableSort(users, getComparator(order, orderBy)).map((user) => (
                    <TableRow key={user.identificador || user.email || user.nome}>
                      <TableCell>{user.nome}</TableCell>
                      <TableCell>
                        {user.roles.length ? (
                          <>
                            {user.roles.map(({ nome }) => nome).join(', ')}
                          </>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.ativo ? 'Ativo' : 'Inativo'}
                          color={user.ativo ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {/* <IconButton onClick={() => handleOpenEditDialog(user)}> */}
                        <IconButton onClick={() => handleSelectUser(user)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleOpenDeleteDialog(user)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Edit Dialog */}
        <Dialog
          open={openEditDialog}
          fullWidth
          maxWidth="sm"
          onClose={handleCloseEditDialog}
        >
          <DialogTitle sx={{ color: '#40685E' }}>Editar Usuário</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Perfil de Acesso</InputLabel>
                <Select
                  multiple
                  value={userToEdit?.des_centro_servico || []}
                  onChange={handleCentrosServicoChange}
                  input={<OutlinedInput label="Perfil de Acesso" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        if (!roles) {
                          return null;
                        }

                        const role = roles.find(role => role.identificadorUnico === id);

                        return (
                          <Chip
                            key={id}
                            label={role?.nome}
                            onDelete={() => handleChipDelete(id)}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {roles && roles.map((role) => (
                    <MenuItem
                      key={role.identificadorUnico}
                      value={role.identificadorUnico}
                    >
                      {role.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSaveEdit}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
          <DialogTitle sx={{ color: '#40685E' }}>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir o usuário {userToDelete?.nome_usuario}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>
              Cancelar
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteUser}>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Fade>
  );
};

export default UserList;